import React, { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, Square } from 'lucide-react';
import clsx from 'clsx';
import { useLanguage } from '../i18n/LanguageContext';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  onStop?: () => void;
  disabled?: boolean;
  isTyping?: boolean;
  suggestions?: string[];
  onSuggestionClick?: (suggestion: string) => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onStop,
  disabled = false,
  isTyping = false,
  suggestions = [],
  onSuggestionClick,
}) => {
  const { t } = useLanguage();
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [message]);

  const handleSubmit = () => {
    if (message.trim() && !disabled && !isTyping) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="border-t border-gray-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
      {suggestions.length > 0 && (
        <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-primary-500 dark:text-indigo-300" />
            <span className="text-sm text-gray-600 dark:text-gray-200">{t('您可以试试问：', 'Try asking:')}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => onSuggestionClick?.(suggestion)}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm transition-colors cursor-pointer border border-gray-200 dark:bg-slate-800 dark:text-gray-100 dark:border-slate-600 dark:hover:bg-slate-700 disabled:cursor-not-allowed"
                disabled={disabled || isTyping}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="p-4">
        <div
          className={clsx(
            'flex items-end gap-2 rounded-2xl border-2 transition-all bg-white dark:bg-slate-950',
            isFocused ? 'border-primary-500 shadow-lg dark:border-indigo-400' : 'border-gray-200 dark:border-slate-600'
          )}
        >
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={t('输入您的问题... (Shift + Enter 换行)', 'Type your question... (Shift + Enter for a new line)')}
            disabled={disabled || isTyping}
            rows={1}
            className="flex-1 max-h-[150px] p-3 bg-transparent border-0 focus:ring-0 resize-none text-gray-800 placeholder-gray-400 dark:text-gray-50 dark:placeholder-gray-400 disabled:cursor-not-allowed"
          />

          <div className="flex items-center gap-1 p-2">
            {isTyping ? (
              <button
                onClick={onStop}
                className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                title={t('停止生成', 'Stop generating')}
              >
                <Square className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!message.trim() || disabled}
                className={clsx(
                  'p-2 rounded-full transition-all',
                  message.trim() && !disabled
                    ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-md hover:shadow-lg dark:bg-indigo-500 dark:hover:bg-indigo-400'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-slate-700 dark:text-slate-400'
                )}
              >
                <Send className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <p className="mt-2 text-xs text-center text-gray-400 dark:text-gray-300">
          {t('英国签证AI助手 - 基于官方信息 - 仅供参考', 'UK Visa AI Assistant - Based on official information - For reference only')}
        </p>
      </div>
    </div>
  );
};
